// =============================================================================
// Library Upload Page — drag-drop → hash → precheck → init → PUT → complete
// =============================================================================
// Files are staged with previews, then hashed (SHA-256, sequential to bound
// memory) and prechecked against the live library BEFORE any bytes move:
// sku/fileName/identical-bytes conflicts and in-selection duplicates surface
// in a conflict panel (side-by-side previews) where the uploader explicitly
// discards or renames — the server never auto-renames (init 409s instead).
// During upload the drop zone is disabled and each file shows live progress.
// On a 403 (expired presigned PUT) we call refresh-url and retry once.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardHeader, Input, Select } from '@tkpod/ui';
import {
  LIBRARY_ALLOWED_MIME_TYPES,
  LIBRARY_UPLOAD_DEFAULT_MAX_FILE_MB,
  type LibraryUploadInitResponse,
  type LibraryUploadPrecheckFileResult,
} from '@tkpod/shared';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import {
  fetchCategories,
  fetchTags,
  flattenCategories,
  libraryKeys,
  precheckUpload,
} from './api';

const ACCEPT = '.png,.jpg,.jpeg,.webp';
const CONCURRENCY = 5;
const MAX_FILE_MB = LIBRARY_UPLOAD_DEFAULT_MAX_FILE_MB;

type FileStatus =
  | 'hashing'
  | 'checking'
  | 'ready'
  | 'conflict'
  | 'queued'
  | 'uploading'
  | 'uploaded'
  | 'error';

interface FileState {
  id: string;
  file: File;
  previewUrl: string;
  progress: number; // 0..100
  status: FileStatus;
  error?: string;
  /** SHA-256 hex of the bytes (computed once, reused across prechecks). */
  contentHash?: string;
  /** Explicit sku set via the conflict-resolution rename flow. */
  skuOverride?: string;
  /** Latest precheck verdict for this file. */
  check?: LibraryUploadPrecheckFileResult;
}

const STATUS_LABEL: Record<FileStatus, string> = {
  hashing: 'Hashing…',
  checking: 'Checking…',
  ready: 'Ready',
  conflict: 'Conflict',
  queued: 'Queued',
  uploading: 'Uploading…',
  uploaded: 'Processing…',
  error: 'Error',
};

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** SHA-256 hex of a file's bytes. Loads the file into memory — callers hash
 * sequentially so the peak is ONE file, never the whole selection. */
async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const CONFLICT_REASON_LABEL: Record<string, string> = {
  sku: 'SKU exists',
  fileName: 'Same file name',
  contentHash: 'Identical bytes',
};

const PUT_MAX_ATTEMPTS = 4;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * PUT one file to its presigned URL, resilient to transient failures. Retries
 * on network errors / timeouts / 5xx with backoff, and on 403 (expired URL)
 * re-signs via refresh-url before retrying. This prevents a brief blip (server
 * restart, network wobble) from silently stranding a file as "File not
 * uploaded" — the single biggest cause of failed grouped uploads.
 */
async function putWithRetry(
  batchId: string,
  assetId: string,
  uploadUrl: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  // Returns the HTTP status, or null for a network error / timeout.
  const attempt = (url: string) =>
    new Promise<number | null>((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.timeout = 120_000;
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
      xhr.onload = () => resolve(xhr.status);
      xhr.onerror = () => resolve(null);
      xhr.ontimeout = () => resolve(null);
      xhr.send(file);
    });

  let url = uploadUrl;
  let lastStatus: number | null = null;
  for (let i = 0; i < PUT_MAX_ATTEMPTS; i++) {
    const status = await attempt(url);
    if (status !== null && status >= 200 && status < 300) return; // success
    lastStatus = status;

    if (status === 403) {
      // Expired presigned URL — re-sign and retry immediately (no backoff).
      try {
        const refreshed = await api.post<{ uploadUrl: string }>(
          `/api/v1/admin/library/uploads/${batchId}/assets/${assetId}/refresh-url`,
          {},
        );
        url = refreshed.uploadUrl;
      } catch {
        /* keep the old url; the retry below will fail and loop */
      }
      continue;
    }
    // Network error (null) or 5xx — reset progress and back off before retry.
    if (i < PUT_MAX_ATTEMPTS - 1) {
      onProgress(0);
      await sleep(1000 * (i + 1));
    }
  }
  throw new Error(`Upload failed after ${PUT_MAX_ATTEMPTS} attempts (${lastStatus ?? 'network error'})`);
}

export function LibraryUploadPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<FileState[]>([]);
  const [batchName, setBatchName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  /** Per-conflict rename drafts (fileId → sku input value). */
  const [renameDrafts, setRenameDrafts] = useState<Record<string, string>>({});
  // Set once the batch is enqueued — the page stays put (no redirect) and the
  // dropzone stays locked until the user starts a new upload.
  const [uploadedBatchId, setUploadedBatchId] = useState<string | null>(null);
  const done = uploadedBatchId !== null;
  const locked = busy || done;
  const uploadedCount = files.filter((f) => f.status === 'uploaded').length;
  const failedCount = files.filter((f) => f.status === 'error').length;
  const conflicts = files.filter((f) => f.status === 'conflict');
  const checkingCount = files.filter((f) => f.status === 'hashing' || f.status === 'checking').length;
  const canUpload = files.length > 0 && conflicts.length === 0 && checkingCount === 0 && !busy;

  const categoriesQuery = useQuery({ queryKey: libraryKeys.categories, queryFn: fetchCategories });
  const categoryOptions = useMemo(
    () => [{ value: '', label: 'No category' }, ...flattenCategories(categoriesQuery.data ?? [])],
    [categoriesQuery.data],
  );
  useQuery({ queryKey: libraryKeys.tags, queryFn: fetchTags }); // warm cache

  // Revoke object URLs ONLY on unmount (removeFile/clearAll revoke eagerly).
  // A [files] dependency would revoke the previous previews on every add,
  // breaking the thumbnails that are still on screen.
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(
    () => () => { filesRef.current.forEach((f) => URL.revokeObjectURL(f.previewUrl)); },
    [],
  );

  const totalBytes = useMemo(() => files.reduce((s, f) => s + f.file.size, 0), [files]);

  const updateFileById = (id: string, patch: Partial<FileState>) =>
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  // ---------------------------------------------------------------------------
  // Precheck orchestration: hash any un-hashed file (sequentially — one file in
  // memory at a time), then dry-run the WHOLE selection against the server.
  // A run token discards stale results when the selection changed mid-run.
  // ---------------------------------------------------------------------------
  const precheckRunRef = useRef(0);

  const runPrecheck = async () => {
    const token = ++precheckRunRef.current;

    // Phase 1: hash sequentially (bounded memory; ~100MB max per file).
    for (const f of filesRef.current) {
      if (f.contentHash || f.status === 'uploaded' || f.status === 'error') continue;
      if (precheckRunRef.current !== token) return;
      updateFileById(f.id, { status: 'hashing' });
      try {
        const hash = await sha256Hex(f.file);
        updateFileById(f.id, { contentHash: hash, status: 'checking' });
      } catch {
        // Hashing unavailable/failed — precheck still works name-only.
        updateFileById(f.id, { status: 'checking' });
      }
    }
    if (precheckRunRef.current !== token) return;

    // Phase 2: one precheck call for the whole (current) selection.
    const snapshot = filesRef.current.filter(
      (f) => f.status !== 'uploaded' && f.status !== 'error',
    );
    if (!snapshot.length) return;
    setFiles((prev) =>
      prev.map((f) =>
        snapshot.some((s) => s.id === f.id) ? { ...f, status: 'checking' } : f,
      ),
    );
    try {
      const res = await precheckUpload({
        files: snapshot.map((f) => ({
          name: f.file.name,
          contentHash: f.contentHash,
          sku: f.skuOverride,
        })),
      });
      if (precheckRunRef.current !== token) return;
      setFiles((prev) =>
        prev.map((f) => {
          const idx = snapshot.findIndex((s) => s.id === f.id);
          if (idx === -1) return f;
          const check = res.files[idx];
          // Blocking rules:
          //  - in-selection duplicate: always (two files can't claim one sku).
          //  - sku match: always (init would 409 — rename must clear it).
          //  - fileName/contentHash-only match: blocks until the user makes an
          //    explicit decision. A rename (skuOverride) IS that decision —
          //    "upload as a new asset" per the panel copy — so once the sku
          //    conflict is gone the file becomes uploadable (the server only
          //    enforces sku uniqueness; name/bytes matches are advisory).
          const skuConflict = check.matches.some((m) => m.matchedOn.includes('sku'));
          const hasConflict =
            check.duplicateInSelectionOf !== null ||
            skuConflict ||
            (check.matches.length > 0 && !f.skuOverride);
          return { ...f, check, status: hasConflict ? 'conflict' : 'ready' };
        }),
      );
    } catch {
      if (precheckRunRef.current !== token) return;
      // Advisory only — init re-validates strictly, so fall back to ready.
      setFiles((prev) =>
        prev.map((f) =>
          snapshot.some((s) => s.id === f.id) ? { ...f, status: 'ready' } : f,
        ),
      );
      toast('Conflict check unavailable — collisions will be caught at upload', 'warning');
    }
  };

  const addFiles = (list: FileList | null) => {
    if (!list || locked) return;
    const allowed = Array.from(list).filter((f) =>
      (LIBRARY_ALLOWED_MIME_TYPES as readonly string[]).includes(f.type),
    );
    const rejected = list.length - allowed.length;
    if (rejected > 0) toast(`${rejected} file(s) skipped — only PNG/JPEG/WebP allowed`, 'warning');
    const oversized = allowed.filter((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (oversized.length) toast(`${oversized.length} file(s) exceed ${MAX_FILE_MB}MB`, 'warning');
    const ok = allowed.filter((f) => f.size <= MAX_FILE_MB * 1024 * 1024);
    if (!ok.length) return;
    setFiles((prev) => [
      ...prev,
      ...ok.map((file) => ({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
        progress: 0,
        status: 'hashing' as const,
      })),
    ]);
    // Defer so filesRef sees the new state on the next tick.
    setTimeout(() => void runPrecheck(), 0);
  };

  const removeFile = (id: string) => {
    if (locked) return;
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
    // Re-evaluate: removing a file can clear another file's in-selection dup.
    setTimeout(() => void runPrecheck(), 0);
  };

  const clearAll = () => {
    if (locked) return;
    precheckRunRef.current += 1; // cancel any in-flight precheck
    files.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setFiles([]);
    setRenameDrafts({});
  };

  const discardConflicting = () => {
    if (locked) return;
    setFiles((prev) => {
      prev.filter((f) => f.status === 'conflict').forEach((f) => URL.revokeObjectURL(f.previewUrl));
      return prev.filter((f) => f.status !== 'conflict');
    });
    setTimeout(() => void runPrecheck(), 0);
  };

  const applyRename = (id: string) => {
    const draft = renameDrafts[id]?.trim();
    if (!draft) return;
    updateFileById(id, { skuOverride: draft });
    setTimeout(() => void runPrecheck(), 0);
  };

  /** After a finished upload: reset the page to stage a fresh batch. */
  const startNewUpload = () => {
    files.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setFiles([]);
    setRenameDrafts({});
    setUploadedBatchId(null);
    setBusy(false);
    setBatchName('');
  };

  const startUpload = async () => {
    if (!canUpload) return;
    setBusy(true);
    precheckRunRef.current += 1; // cancel any in-flight precheck
    const staged = filesRef.current;
    setFiles((prev) => prev.map((f) => ({ ...f, status: 'queued', progress: 0 })));
    try {
      const init = await api.post<LibraryUploadInitResponse>('/api/v1/admin/library/uploads/init', {
        batchName: batchName.trim() || undefined,
        defaultCategoryId: categoryId || undefined,
        defaultTags: tagsText.split(',').map((s) => s.trim()).filter(Boolean),
        files: staged.map((f) => ({
          name: f.file.name,
          size: f.file.size,
          mimeType: f.file.type,
          sku: f.skuOverride,
        })),
      });

      const queue = init.files.map((meta, idx) => ({ meta, state: staged[idx] }));
      let cursor = 0;
      let failedUploads = 0;
      const worker = async () => {
        while (cursor < queue.length) {
          const { meta, state } = queue[cursor++];
          updateFileById(state.id, { status: 'uploading' });
          try {
            await putWithRetry(init.batchId, meta.assetId, meta.uploadUrl, state.file, (pct) =>
              updateFileById(state.id, { progress: pct }),
            );
            updateFileById(state.id, { status: 'uploaded', progress: 100 });
          } catch (e) {
            failedUploads += 1;
            updateFileById(state.id, { status: 'error', error: (e as Error).message });
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));

      // Surface upload failures explicitly — otherwise they'd only appear as
      // "File not uploaded" FAILED rows in batch review after processing.
      if (failedUploads > 0) {
        toast(
          `${failedUploads} file(s) could not be uploaded and will show as FAILED. Delete them in batch review and retry.`,
          'error',
        );
      }

      await api.post('/api/v1/admin/library/uploads/complete', { batchId: init.batchId });
      toast('Upload complete — processing started', 'success');
      // Stay on the page: mark the batch as enqueued and keep the dropzone
      // locked. The user chooses what to do next (review or upload more).
      setUploadedBatchId(init.batchId);
      setBusy(false);
    } catch (e) {
      toast((e as Error).message, 'error');
      // Re-run precheck so a 409 (collision raced in) re-renders the panel.
      setFiles((prev) =>
        prev.map((f) =>
          f.status === 'queued' || f.status === 'uploading' ? { ...f, status: 'checking' } : f,
        ),
      );
      setBusy(false);
      setTimeout(() => void runPrecheck(), 0);
    }
  };

  return (
    <div className="space-y-6 pb-24">
      <Button variant="ghost" size="sm" onClick={() => navigate('/library')}>← Back to library</Button>

      <Card>
        <CardHeader title="Upload assets" description="Drag & drop images (PNG, JPEG, WebP). Files are checked against the library for duplicates before anything uploads." />

        {/* Batch options */}
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <Input placeholder="Batch name (optional)" value={batchName} onChange={(e) => setBatchName(e.target.value)} disabled={locked} />
          <Select options={categoryOptions} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} disabled={locked} />
          <Input placeholder="Default tags (comma-separated)" value={tagsText} onChange={(e) => setTagsText(e.target.value)} disabled={locked} />
        </div>

        {/* Completion banner — stays on the page (no redirect) */}
        {done && (
          <div className="mt-4 flex flex-col gap-3 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <svg className="h-6 w-6 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {uploadedCount} file{uploadedCount === 1 ? '' : 's'} uploaded — processing in the background
                  {failedCount > 0 ? ` · ${failedCount} failed` : ''}
                </p>
                <p className="text-xs text-text-secondary">Assets appear in the library once processing finishes.</p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate(`/library/uploads/${uploadedBatchId}`)}>
                Go to batch review
              </Button>
              <Button size="sm" onClick={startNewUpload}>Upload more</Button>
            </div>
          </div>
        )}

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); if (!locked) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          onClick={() => !locked && inputRef.current?.click()}
          className={[
            'mt-4 rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors',
            locked
              ? 'cursor-not-allowed border-brand-900 bg-surface-tertiary/40 opacity-60'
              : dragOver
                ? 'cursor-pointer border-brand-500 bg-brand-500/5'
                : 'cursor-pointer border-brand-800 hover:border-brand-600',
          ].join(' ')}
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/10">
            <svg className="h-6 w-6 text-brand-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
            </svg>
          </div>
          <p className="mt-3 text-sm font-medium text-text-primary">
            {busy ? 'Uploading… please wait' : done ? 'Upload complete' : 'Drag & drop files here'}
          </p>
          {!locked && (
            <p className="mt-1 text-sm text-text-secondary">
              or <span className="font-medium text-brand-400 underline">click to browse</span> your folders
            </p>
          )}
          <p className="mt-2 text-xs uppercase tracking-wide text-text-tertiary">Max file size: {MAX_FILE_MB}MB</p>
          <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" disabled={locked} onChange={(e) => addFiles(e.target.files)} />
        </div>

        {/* Conflict panel — must be resolved (discard/rename) before upload */}
        {conflicts.length > 0 && !done && (
          <div className="mt-6 overflow-hidden rounded-xl border border-red-500/40">
            <div className="flex items-center justify-between border-b border-red-500/30 bg-red-500/10 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-red-400">
                  {conflicts.length} conflict{conflicts.length === 1 ? '' : 's'} — resolve before uploading
                </p>
                <p className="text-xs text-text-secondary">
                  Discard the duplicates, or rename to upload as a new asset. Nothing is uploaded automatically.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={discardConflicting}>
                Discard all conflicting
              </Button>
            </div>
            <div className="divide-y divide-brand-900">
              {conflicts.map((f) => {
                const check = f.check!;
                const firstMatch = check.matches[0];
                return (
                  <div key={f.id} className="px-4 py-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                      {/* New file (left) */}
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-[#bdbdbd]">
                          <img src={f.previewUrl} alt={f.file.name} className="max-h-full max-w-full object-contain" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-text-primary">{f.file.name}</p>
                          <p className="text-xs text-text-tertiary">
                            New upload · sku <span className="font-mono">{check.derivedSku}</span>
                          </p>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {check.duplicateInSelectionOf ? (
                              <span className="rounded border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-[11px] text-red-400">
                                Duplicate of “{check.duplicateInSelectionOf}” in this selection
                              </span>
                            ) : (
                              firstMatch?.matchedOn.map((r) => (
                                <span key={r} className="rounded border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-[11px] text-red-400">
                                  {CONFLICT_REASON_LABEL[r] ?? r}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Existing asset (right) */}
                      {firstMatch && (
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-[#bdbdbd]">
                            {firstMatch.thumbUrl ? (
                              <img src={firstMatch.thumbUrl} alt={firstMatch.sku} className="max-h-full max-w-full object-contain" />
                            ) : (
                              <span className="text-[10px] text-text-tertiary">No preview</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm text-text-primary">
                              In library: <span className="font-mono">{firstMatch.sku}</span>
                            </p>
                            <p className="truncate text-xs text-text-tertiary">
                              {firstMatch.fileName} · {firstMatch.status}
                            </p>
                            <a
                              href={`/library/assets/${firstMatch.assetId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs font-medium text-brand-400 hover:underline"
                            >
                              Open existing asset ↗
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex shrink-0 items-center gap-2">
                        {!check.duplicateInSelectionOf && (
                          <>
                            <Input
                              placeholder="New sku…"
                              value={renameDrafts[f.id] ?? ''}
                              onChange={(e) => setRenameDrafts((d) => ({ ...d, [f.id]: e.target.value }))}
                              onKeyDown={(e) => e.key === 'Enter' && applyRename(f.id)}
                              className="w-36"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!renameDrafts[f.id]?.trim()}
                              onClick={() => applyRename(f.id)}
                            >
                              Rename
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => removeFile(f.id)}>
                          Discard
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Queue */}
        {files.length > 0 && (
          <div className="mt-6 overflow-hidden rounded-xl border border-brand-900">
            <div className="flex items-center justify-between border-b border-brand-900 bg-surface-tertiary/40 px-4 py-3">
              <span className="text-sm font-semibold text-text-primary">Queue ({files.length} items)</span>
              <span className="text-xs text-text-tertiary">
                {checkingCount > 0 ? `Checking ${checkingCount}… · ` : ''}
                {formatSize(totalBytes)} total
              </span>
            </div>
            <div className="divide-y divide-brand-900">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-[#bdbdbd]">
                    <img src={f.previewUrl} alt={f.file.name} className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-text-primary">
                      {f.file.name}
                      {f.skuOverride && (
                        <span className="ml-2 text-xs text-text-tertiary">
                          → sku <span className="font-mono">{f.check?.derivedSku ?? f.skuOverride}</span>
                        </span>
                      )}
                    </p>
                    {f.check?.variantSuggestion && f.status !== 'conflict' && (
                      <p className="truncate text-xs text-brand-400">
                        Variant of “{f.check.variantSuggestion.baseSku}” — will be auto-linked (undo in review)
                      </p>
                    )}
                    {/* Renamed past a name/bytes match: explicit "new asset" decision. */}
                    {f.status !== 'conflict' && f.skuOverride && (f.check?.matches.length ?? 0) > 0 && (
                      <p className="truncate text-xs text-amber-500">
                        Matches “{f.check!.matches[0].sku}” ({f.check!.matches[0].matchedOn.includes('contentHash') ? 'identical bytes' : 'same file name'}) — uploading as a new asset
                      </p>
                    )}
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-tertiary">
                      <div
                        className={[
                          'h-full transition-all',
                          f.status === 'error' || f.status === 'conflict'
                            ? 'bg-red-500'
                            : f.status === 'uploaded'
                              ? 'bg-green-500'
                              : 'bg-brand-500',
                        ].join(' ')}
                        style={{ width: `${f.status === 'uploaded' ? 100 : f.status === 'conflict' ? 100 : f.progress}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-24 shrink-0 text-right">
                    <p className={['text-sm font-semibold', f.status === 'error' || f.status === 'conflict' ? 'text-red-400' : 'text-text-primary'].join(' ')}>
                      {f.status === 'uploaded' ? '100%' : f.status === 'conflict' ? '—' : `${f.progress}%`}
                    </p>
                    <p className="text-xs italic text-text-tertiary">{f.error ?? STATUS_LABEL[f.status]}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(f.id)}
                    disabled={locked}
                    className="shrink-0 rounded p-1 text-text-tertiary hover:text-red-400 disabled:opacity-30"
                    aria-label="Remove file"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Sticky action bar — hidden once the batch is enqueued (banner takes over) */}
      {files.length > 0 && !done && (
        <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-brand-900 bg-surface-secondary/95 px-6 py-4 backdrop-blur lg:left-64">
          <div className="mx-auto flex max-w-5xl items-center justify-between">
            <p className="text-sm text-text-secondary">
              <span className="font-semibold text-text-primary">{files.length} files</span> selected ({formatSize(totalBytes)})
              {conflicts.length > 0 && (
                <span className="text-red-400"> · {conflicts.length} conflict{conflicts.length === 1 ? '' : 's'}</span>
              )}
              {!busy && (
                <>
                  {' · '}
                  <button type="button" onClick={() => inputRef.current?.click()} className="font-medium text-brand-400 hover:underline">
                    Select more files
                  </button>
                </>
              )}
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={clearAll} disabled={locked}>Clear All</Button>
              <Button
                loading={busy}
                disabled={!canUpload}
                title={
                  conflicts.length > 0
                    ? 'Resolve the conflicts first'
                    : checkingCount > 0
                      ? 'Checking for duplicates…'
                      : undefined
                }
                onClick={() => void startUpload()}
              >
                Upload {files.length} File{files.length === 1 ? '' : 's'} →
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
